// The 3D flight view: a MapLibre custom WebGL layer drawing each contact at
// its REPORTED altitude — a billboarded plane sprite rotated to its track, a
// vertical stalk tying it to its ground position, and the trail as a ribbon
// whose vertices climb and descend with the aircraft, coloured by the same
// altitude ramp the 2D trail uses.
//
// The shape of this module follows domes.ts, the codebase's standing custom
// 3D layer: vertices are absolute Mercator coordinates from
// MercatorCoordinate.fromLngLat (which takes altitude in metres), the matrix
// is defaultProjectionData.mainMatrix, blending is premultiplied, and on the
// globe projection it bails rather than feeding Mercator coordinates through
// the globe matrix (aircraft.ts falls back to the flat layers there).
//
// DATA FLOWS ONE WAY. aircraft.ts computes everything display-shaped (splined
// paths, altitude colours, the sprite atlas) and hands plain arrays to
// setData; this module owns only geometry building and GL. That is what keeps
// the import graph acyclic — aircraft.ts imports this, never the reverse.
//
// Altitude is TRUE SCALE, deliberately not exaggerated: a cruise altitude of
// 11 km is visibly tall at any zoom the layer is read at, and an exaggerated
// stalk would be a claim about the sky the data does not make — the same
// honesty rule the feed's "inferred" wording follows.

import * as maplibregl from 'maplibre-gl';

const LAYER_ID = 'aircraft-3d';

export interface Contact3d {
  lon: number;
  lat: number;
  altM: number;          // display altitude, metres MSL
  trackDeg: number;      // heading clockwise from north
  mil: boolean;
  alpha: number;         // 1, or the stale fade
  props: Record<string, unknown>;   // popup payload, verbatim
}

export interface Path3d {
  pts: Array<[number, number]>;     // splined lon/lat
  altM: number[];                   // one per vertex
  rgb: Array<[number, number, number]>;  // one per vertex, 0..1
}

// --- shaders -----------------------------------------------------------------

// Screen-space extruded ribbon: both segment endpoints ride in each vertex,
// the shader projects them, takes the screen-space normal and pushes the
// vertex sideways by the half-width. Wide lines the portable way — GPUs cap
// gl.lineWidth at 1px (domes.ts carries the same note).
const LINE_VERT = `
uniform mat4 u_matrix;
uniform vec2 u_viewport;
uniform float u_dpr;
attribute vec3 a_posA;
attribute vec3 a_posB;
attribute float a_side;
attribute float a_end;
attribute float a_width;
attribute vec4 a_color;
varying vec4 v_color;
void main() {
  vec4 pa = u_matrix * vec4(a_posA, 1.0);
  vec4 pb = u_matrix * vec4(a_posB, 1.0);
  vec2 sa = pa.xy / pa.w * u_viewport;
  vec2 sb = pb.xy / pb.w * u_viewport;
  vec2 d = sb - sa;
  float len = max(length(d), 1e-4);
  vec2 nrm = vec2(-d.y, d.x) / len;
  vec4 p = mix(pa, pb, a_end);
  p.xy += nrm * a_side * (a_width * u_dpr) * 2.0 / u_viewport * p.w;
  gl_Position = p;
  v_color = a_color;
}`;
const LINE_FRAG = `
precision mediump float;
varying vec4 v_color;
void main() { gl_FragColor = vec4(v_color.rgb * v_color.a, v_color.a); }`;

// A REAL MESH, not a billboard — the aircraft is a solid body rotated in
// WORLD space, so a pitched camera sees it in three-quarter view and the
// heading is right by construction. The previous billboard rotated in screen
// space and had to undo the map bearing by hand; it also flipped y twice and
// pointed every nose due south. World-space orientation cannot make that
// mistake, because the basis vectors ARE the compass.
//
// THE BASIS, and the one thing to get right here: Mercator is +x east, +y
// SOUTH, +z up. A heading is clockwise from north, so it points (sin east,
// cos north) = (sin, -cos) in Mercator. orientToMercator() below is the same
// arithmetic in TypeScript and the test pins it — keep the two together.
const MESH_VERT = `
uniform mat4 u_matrix;
uniform float u_scale;
uniform vec3 u_light;
attribute vec3 a_center;
attribute vec3 a_model;
attribute vec3 a_normal;
attribute float a_rot;
attribute float a_alpha;
attribute vec3 a_color;
varying vec4 v_color;
void main() {
  float s = sin(a_rot);
  float c = cos(a_rot);
  vec3 right = vec3(c, s, 0.0);
  vec3 nose  = vec3(s, -c, 0.0);
  vec3 up    = vec3(0.0, 0.0, 1.0);
  vec3 off = (right * a_model.x + nose * a_model.y + up * a_model.z) * u_scale;
  vec3 n = normalize(right * a_normal.x + nose * a_normal.y + up * a_normal.z);
  // Lambert against a fixed WORLD light, so turning the map lights the model
  // from a steady direction instead of the shading spinning with the plane.
  float shade = 0.42 + 0.58 * max(dot(n, u_light), 0.0);
  gl_Position = u_matrix * vec4(a_center + off, 1.0);
  v_color = vec4(a_color * shade, a_alpha);
}`;
const MESH_FRAG = `
precision mediump float;
varying vec4 v_color;
void main() { gl_FragColor = vec4(v_color.rgb * v_color.a, v_color.a); }`;

// --- the aircraft model ------------------------------------------------------
//
// Model space is x right (starboard), y forward (nose at +1, tail at −1), z
// up, and it is UNITLESS: the scale applied at render time is screen-derived,
// not physical. A 60 m airframe drawn to scale at theatre zoom is a fraction
// of a pixel, so this is an ICON with three dimensions, never a measurement —
// altitude is the thing kept true to scale, because that is the number a
// reader would actually take off the picture.

type V3 = [number, number, number];

const sub3 = (a: V3, b: V3): V3 => [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
const add3 = (a: V3, b: V3): V3 => [a[0] + b[0], a[1] + b[1], a[2] + b[2]];
const neg3 = (a: V3): V3 => [-a[0], -a[1], -a[2]];
const dot3 = (a: V3, b: V3): number => a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
const cross3 = (a: V3, b: V3): V3 =>
  [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]];
function unit3(v: V3): V3 {
  const l = Math.hypot(v[0], v[1], v[2]);
  return l < 1e-9 ? [0, 0, 1] : [v[0] / l, v[1] / l, v[2] / l];
}

interface MeshBuild { pos: number[]; nrm: number[] }

/** One triangle. `hint` is a direction the outward normal must agree with —
 *  which is what lets the geometry below be written in whatever winding reads
 *  clearest without a backwards-facing (and therefore black) panel. Culling
 *  is off, so winding never affects visibility, only lighting. */
function tri(m: MeshBuild, a: V3, b: V3, c: V3, hint: V3): void {
  let n = unit3(cross3(sub3(b, a), sub3(c, a)));
  if (dot3(n, hint) < 0) n = neg3(n);
  for (const p of [a, b, c]) {
    m.pos.push(p[0], p[1], p[2]);
    m.nrm.push(n[0], n[1], n[2]);
  }
}

function quad(m: MeshBuild, a: V3, b: V3, c: V3, d: V3, hint: V3): void {
  tri(m, a, b, c, hint);
  tri(m, a, c, d, hint);
}

/** A thin slab from four mid-plane corners given in perimeter order — wings,
 *  stabilisers and the fin are all this shape, differing only in which way is
 *  "up" through the plate. Each side face takes its hint from the plate's own
 *  centroid, so an edge is lit from outside whichever way it faces. */
function plate(m: MeshBuild, c: [V3, V3, V3, V3], thick: number, up: V3): void {
  const u = unit3(up);
  const h: V3 = [u[0] * thick / 2, u[1] * thick / 2, u[2] * thick / 2];
  const top = c.map((p) => add3(p, h)) as [V3, V3, V3, V3];
  const bot = c.map((p) => sub3(p, h)) as [V3, V3, V3, V3];
  quad(m, top[0], top[1], top[2], top[3], u);
  quad(m, bot[3], bot[2], bot[1], bot[0], neg3(u));
  const cen: V3 = [(c[0][0] + c[1][0] + c[2][0] + c[3][0]) / 4,
                   (c[0][1] + c[1][1] + c[2][1] + c[3][1]) / 4,
                   (c[0][2] + c[1][2] + c[2][2] + c[3][2]) / 4];
  for (let i = 0; i < 4; i++) {
    const j = (i + 1) % 4;
    const mid: V3 = [(c[i]![0] + c[j]![0]) / 2, (c[i]![1] + c[j]![1]) / 2,
                     (c[i]![2] + c[j]![2]) / 2];
    quad(m, top[i]!, top[j]!, bot[j]!, bot[i]!, unit3(sub3(mid, cen)));
  }
}

const FUSELAGE: Array<[number, number]> = [   // [y, radius]
  [-1.00, 0.028], [-0.55, 0.088], [0.10, 0.105], [0.62, 0.088], [1.00, 0.022],
];
const SIDES = 8;

/** The airframe, built once at module load and reused by every contact.
 *  Exported so the test can assert the thing has volume and unit normals. */
export function planeMesh(): { pos: Float32Array; nrm: Float32Array } {
  const m: MeshBuild = { pos: [], nrm: [] };
  const ring = (y: number, r: number, k: number): V3 =>
    [r * Math.cos(2 * Math.PI * k / SIDES), y, r * Math.sin(2 * Math.PI * k / SIDES)];

  for (let i = 0; i < FUSELAGE.length - 1; i++) {
    const [y0, r0] = FUSELAGE[i]!;
    const [y1, r1] = FUSELAGE[i + 1]!;
    for (let k = 0; k < SIDES; k++) {
      const a = 2 * Math.PI * (k + 0.5) / SIDES;
      quad(m, ring(y0, r0, k), ring(y0, r0, k + 1),
              ring(y1, r1, k + 1), ring(y1, r1, k),
           [Math.cos(a), 0, Math.sin(a)]);
    }
  }
  const nose: V3 = [0, 1.06, 0], tail: V3 = [0, -1.03, 0];
  const [yN, rN] = FUSELAGE[FUSELAGE.length - 1]!;
  const [yT, rT] = FUSELAGE[0]!;
  for (let k = 0; k < SIDES; k++) {
    tri(m, nose, ring(yN, rN, k), ring(yN, rN, k + 1), [0, 1, 0]);
    tri(m, tail, ring(yT, rT, k + 1), ring(yT, rT, k), [0, -1, 0]);
  }

  const up: V3 = [0, 0, 1];
  for (const s of [1, -1]) {
    // Swept wing, mounted a touch low on the fuselage.
    plate(m, [[0.09 * s, 0.30, -0.012], [0.90 * s, -0.08, -0.012],
              [0.90 * s, -0.32, -0.012], [0.09 * s, -0.22, -0.012]], 0.032, up);
    // Horizontal stabiliser.
    plate(m, [[0.06 * s, -0.72, 0.01], [0.33 * s, -0.86, 0.01],
              [0.33 * s, -0.99, 0.01], [0.06 * s, -0.95, 0.01]], 0.022, up);
  }
  // Vertical fin — the plate's "up" is the x axis, which is what makes the
  // same builder produce a surface standing on edge.
  plate(m, [[0, -0.52, 0.05], [0, -0.86, 0.42],
            [0, -1.00, 0.42], [0, -1.00, 0.05]], 0.024, [1, 0, 0]);

  return { pos: new Float32Array(m.pos), nrm: new Float32Array(m.nrm) };
}

/** The vertex shader's basis arithmetic, in TypeScript, so the orientation
 *  can be pinned by a test — a heading drawn 180° out is exactly the bug this
 *  replaced, and it is invisible in code review either way. Keep in step with
 *  MESH_VERT above; they are two copies of three lines on purpose. */
export function orientToMercator(model: V3, headingDeg: number): V3 {
  const t = headingDeg * Math.PI / 180;
  const s = Math.sin(t), c = Math.cos(t);
  return [model[0] * c + model[1] * s,
          model[0] * s - model[1] * c,
          model[2]];
}

const MODEL = planeMesh();
const MODEL_VERTS = MODEL.pos.length / 3;

// --- module state ------------------------------------------------------------

const LINE_STRIDE = 13;    // posA(3) posB(3) side end width rgba(4)
const MESH_STRIDE = 14;    // center(3) model(3) normal(3) rot alpha rgb(3)

// Mil amber / civil blue, the flat layer's two colours as linear rgb.
const MIL_RGB: V3 = [0xe8 / 255, 0x91 / 255, 0x3c / 255];
const CIV_RGB: V3 = [0x5f / 255, 0xa8 / 255, 0xd3 / 255];

// Light from above and the south-east — a fixed WORLD direction, so the model
// is shaded by where it points rather than by where the camera is.
const LIGHT: V3 = unit3([0.30, 0.50, 0.80]);

let visible = false;
let lineData = new Float32Array(0);
let lineVerts = 0;
let meshData = new Float32Array(0);
let meshVerts = 0;
let dirty = false;
// For the click hit-test: each contact's Mercator position and its payload,
// plus the matrix the last frame actually rendered with.
let hitContacts: Array<{ x: number; y: number; z: number;
  lngLat: [number, number]; props: Record<string, unknown> }> = [];
let lastMatrix: Float32Array | null = null;

function compile(gl: WebGLRenderingContext, src: string, type: number): WebGLShader {
  const s = gl.createShader(type)!;
  gl.shaderSource(s, src);
  gl.compileShader(s);
  if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) {
    console.error('[aircraft3d] shader compile failed:', gl.getShaderInfoLog(s));
  }
  return s;
}

function link(gl: WebGLRenderingContext, vert: string, frag: string): WebGLProgram {
  const p = gl.createProgram()!;
  gl.attachShader(p, compile(gl, vert, gl.VERTEX_SHADER));
  gl.attachShader(p, compile(gl, frag, gl.FRAGMENT_SHADER));
  gl.linkProgram(p);
  if (!gl.getProgramParameter(p, gl.LINK_STATUS)) {
    console.error('[aircraft3d] program link failed:', gl.getProgramInfoLog(p));
  }
  return p;
}

/** Mercator units per model unit, so the aircraft holds a roughly constant
 *  SCREEN size — the flat layer's icon-size ramp, in world units.
 *
 *  MapLibre's world is 1 Mercator unit across and 512·2^zoom CSS px wide, so
 *  dividing by that is what turns a pixel size into a world size. Perspective
 *  still shrinks a distant contact, which is the depth cue a pitched view
 *  wants, and is why this is not corrected per vertex. */
function modelScale(zoom: number): number {
  const f = zoom <= 4 ? 0.65 : zoom >= 10 ? 1.15 : 0.65 + (zoom - 4) / 6 * 0.5;
  return (22 * f) / (512 * Math.pow(2, zoom));
}

// --- geometry building -------------------------------------------------------

function pushSegment(out: number[], ax: number, ay: number, az: number,
                     bx: number, by: number, bz: number, width: number,
                     ca: [number, number, number, number],
                     cb: [number, number, number, number]): void {
  // Two triangles. Every vertex carries BOTH endpoints — the shader picks by
  // a_end and offsets by a_side — and per-vertex colour so a ribbon shades
  // continuously along the climb instead of banding per segment.
  const v = (side: number, end: number, c: [number, number, number, number]) => {
    out.push(ax, ay, az, bx, by, bz, side, end, width, c[0], c[1], c[2], c[3]);
  };
  v(-1, 0, ca); v(1, 0, ca); v(-1, 1, cb);
  v(1, 0, ca); v(1, 1, cb); v(-1, 1, cb);
}

/** Rebuild the CPU-side buffers. Cheap enough to run per poll (a busy zone is
 *  a few hundred contacts), and doing it here keeps render() upload-only. */
export function setData(contacts: Contact3d[], paths: Path3d[]): void {
  const lines: number[] = [];
  for (const p of paths) {
    for (let i = 0; i < p.pts.length - 1; i++) {
      const a = maplibregl.MercatorCoordinate.fromLngLat(p.pts[i]!, p.altM[i]!);
      const b = maplibregl.MercatorCoordinate.fromLngLat(p.pts[i + 1]!, p.altM[i + 1]!);
      const ca = p.rgb[i]!, cb = p.rgb[i + 1]!;
      pushSegment(lines, a.x, a.y, a.z, b.x, b.y, b.z, 1.6,
        [ca[0], ca[1], ca[2], 0.85], [cb[0], cb[1], cb[2], 0.85]);
    }
  }
  hitContacts = [];
  // One copy of the airframe per contact. Instancing would send the mesh once
  // and the per-contact values as a divisor-1 buffer, but that needs a WebGL2
  // path plus an ANGLE_instanced_arrays fallback, and the counts here are a
  // few hundred at most — a flat buffer is the same picture with less to get
  // wrong. Revisit if a zone ever carries thousands.
  const mesh = new Float32Array(contacts.length * MODEL_VERTS * MESH_STRIDE);
  let mi = 0;
  for (const c of contacts) {
    const top = maplibregl.MercatorCoordinate.fromLngLat([c.lon, c.lat], c.altM);
    const base = maplibregl.MercatorCoordinate.fromLngLat([c.lon, c.lat], 0);
    // The stalk is what makes altitude READABLE rather than merely rendered —
    // without it a pitched view cannot tell high-and-near from low-and-far.
    const sc: [number, number, number, number] = c.mil
      ? [0.91, 0.57, 0.24, 0.35] : [0.37, 0.66, 0.83, 0.35];
    pushSegment(lines, base.x, base.y, base.z, top.x, top.y, top.z, 0.75, sc, sc);
    const rot = c.trackDeg * Math.PI / 180;
    const col = c.mil ? MIL_RGB : CIV_RGB;
    for (let v = 0; v < MODEL_VERTS; v++) {
      mesh[mi++] = top.x; mesh[mi++] = top.y; mesh[mi++] = top.z;
      mesh[mi++] = MODEL.pos[v * 3]!;
      mesh[mi++] = MODEL.pos[v * 3 + 1]!;
      mesh[mi++] = MODEL.pos[v * 3 + 2]!;
      mesh[mi++] = MODEL.nrm[v * 3]!;
      mesh[mi++] = MODEL.nrm[v * 3 + 1]!;
      mesh[mi++] = MODEL.nrm[v * 3 + 2]!;
      mesh[mi++] = rot;
      mesh[mi++] = c.alpha;
      mesh[mi++] = col[0]; mesh[mi++] = col[1]; mesh[mi++] = col[2];
    }
    hitContacts.push({ x: top.x, y: top.y, z: top.z,
      lngLat: [c.lon, c.lat], props: c.props });
  }
  lineData = new Float32Array(lines);
  lineVerts = lines.length / LINE_STRIDE;
  meshData = mesh;
  meshVerts = contacts.length * MODEL_VERTS;
  dirty = true;
}

export function setVisible(on: boolean): void {
  visible = on;
}

export function isInstalled(map: maplibregl.Map): boolean {
  return !!map.getLayer(LAYER_ID);
}

/** Screen-space hit test against the last rendered frame — custom WebGL is
 *  not natively pickable (domes.ts solves this with a ground footprint layer,
 *  but a plane's elevated sprite sits far from its ground point when pitched,
 *  so the test has to happen where the sprite was actually drawn). */
export function contactAt(map: maplibregl.Map, px: number, py: number):
    { lngLat: [number, number]; props: Record<string, unknown> } | null {
  if (!visible || !lastMatrix || !hitContacts.length) return null;
  const w = map.getCanvas().clientWidth, h = map.getCanvas().clientHeight;
  const m = lastMatrix;
  let best: typeof hitContacts[number] | null = null;
  let bestD = 18 * 18;   // ~ the sprite's own radius plus finger slack
  for (const c of hitContacts) {
    const cw = m[3]! * c.x + m[7]! * c.y + m[11]! * c.z + m[15]!;
    if (cw <= 0) continue;   // behind the camera
    const cx = (m[0]! * c.x + m[4]! * c.y + m[8]! * c.z + m[12]!) / cw;
    const cy = (m[1]! * c.x + m[5]! * c.y + m[9]! * c.z + m[13]!) / cw;
    const sx = (cx + 1) / 2 * w, sy = (1 - cy) / 2 * h;
    const d = (sx - px) * (sx - px) + (sy - py) * (sy - py);
    if (d < bestD) { bestD = d; best = c; }
  }
  return best ? { lngLat: best.lngLat, props: best.props } : null;
}

// --- the layer ---------------------------------------------------------------

export function install(map: maplibregl.Map): void {
  if (map.getLayer(LAYER_ID)) return;
  let lineProg: WebGLProgram | null = null;
  let meshProg: WebGLProgram | null = null;
  let lineVbo: WebGLBuffer | null = null;
  let meshVbo: WebGLBuffer | null = null;
  const loc: Record<string, number> = {};
  const uni: Record<string, WebGLUniformLocation | null> = {};

  const layer: maplibregl.CustomLayerInterface = {
    id: LAYER_ID, type: 'custom', renderingMode: '3d',
    onAdd(_m, gl) {
      lineProg = link(gl, LINE_VERT, LINE_FRAG);
      meshProg = link(gl, MESH_VERT, MESH_FRAG);
      for (const a of ['a_posA', 'a_posB', 'a_side', 'a_end', 'a_width', 'a_color']) {
        loc[`l_${a}`] = gl.getAttribLocation(lineProg, a);
      }
      for (const a of ['a_center', 'a_model', 'a_normal', 'a_rot', 'a_alpha', 'a_color']) {
        loc[`m_${a}`] = gl.getAttribLocation(meshProg, a);
      }
      for (const u of ['u_matrix', 'u_viewport', 'u_dpr']) {
        uni[`l_${u}`] = gl.getUniformLocation(lineProg, u);
      }
      for (const u of ['u_matrix', 'u_scale', 'u_light']) {
        uni[`m_${u}`] = gl.getUniformLocation(meshProg, u);
      }
      lineVbo = gl.createBuffer();
      meshVbo = gl.createBuffer();
    },
    render(gl, args) {
      if (!visible || !lineProg || !meshProg) return;
      // Mercator vertices through the globe matrix would land anywhere;
      // domes.ts's rule, including the "undefined means Mercator" caveat.
      if (map.getProjection()?.type === 'globe') return;
      const matrix = new Float32Array(
        (args as unknown as { defaultProjectionData: { mainMatrix: number[] } })
          .defaultProjectionData.mainMatrix);
      lastMatrix = matrix;
      const vw = gl.drawingBufferWidth, vh = gl.drawingBufferHeight;
      const dpr = window.devicePixelRatio || 1;

      gl.enable(gl.BLEND);
      gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
      // Trails and stalks are flat overlays: no depth at all, so a ribbon
      // never hides the aircraft it belongs to. The solid model that follows
      // turns depth back on for its own sake.
      gl.disable(gl.DEPTH_TEST);
      gl.depthMask(false);
      gl.disable(gl.CULL_FACE);

      if (lineVerts) {
        gl.useProgram(lineProg);
        gl.bindBuffer(gl.ARRAY_BUFFER, lineVbo);
        if (dirty) gl.bufferData(gl.ARRAY_BUFFER, lineData, gl.DYNAMIC_DRAW);
        gl.uniformMatrix4fv(uni['l_u_matrix']!, false, matrix);
        gl.uniform2f(uni['l_u_viewport']!, vw, vh);
        gl.uniform1f(uni['l_u_dpr']!, dpr);
        const S = LINE_STRIDE * 4;
        const enable = (name: string, size: number, off: number) => {
          const a = loc[name]!;
          gl.enableVertexAttribArray(a);
          gl.vertexAttribPointer(a, size, gl.FLOAT, false, S, off);
        };
        enable('l_a_posA', 3, 0);
        enable('l_a_posB', 3, 12);
        enable('l_a_side', 1, 24);
        enable('l_a_end', 1, 28);
        enable('l_a_width', 1, 32);
        enable('l_a_color', 4, 36);
        gl.drawArrays(gl.TRIANGLES, 0, lineVerts);
      }

      if (meshVerts) {
        gl.useProgram(meshProg);
        gl.bindBuffer(gl.ARRAY_BUFFER, meshVbo);
        if (dirty) gl.bufferData(gl.ARRAY_BUFFER, meshData, gl.DYNAMIC_DRAW);
        // A solid body has to occlude ITSELF or the far wing paints over the
        // near one. Depth WRITE, not just test — and culling stays off, so a
        // triangle wound the wrong way is a lighting question the outward
        // hints already answer, never a hole in the aircraft.
        gl.enable(gl.DEPTH_TEST);
        gl.depthFunc(gl.LEQUAL);
        gl.depthMask(true);
        gl.uniformMatrix4fv(uni['m_u_matrix']!, false, matrix);
        gl.uniform1f(uni['m_u_scale']!, modelScale(map.getZoom()));
        gl.uniform3f(uni['m_u_light']!, LIGHT[0], LIGHT[1], LIGHT[2]);
        const S = MESH_STRIDE * 4;
        const enable = (name: string, size: number, off: number) => {
          const a = loc[name]!;
          gl.enableVertexAttribArray(a);
          gl.vertexAttribPointer(a, size, gl.FLOAT, false, S, off);
        };
        enable('m_a_center', 3, 0);
        enable('m_a_model', 3, 12);
        enable('m_a_normal', 3, 24);
        enable('m_a_rot', 1, 36);
        enable('m_a_alpha', 1, 40);
        enable('m_a_color', 3, 44);
        gl.drawArrays(gl.TRIANGLES, 0, meshVerts);
      }
      dirty = false;
      // Hand the context back the way MapLibre's own layers expect it.
      gl.enable(gl.DEPTH_TEST);
      gl.depthFunc(gl.LESS);
      gl.depthMask(true);
    },
    onRemove(_m, gl) {
      for (const p of [lineProg, meshProg]) if (p) gl.deleteProgram(p);
      for (const b of [lineVbo, meshVbo]) if (b) gl.deleteBuffer(b);
    },
  };
  map.addLayer(layer);
}
